import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  Grid,
  Switch,
} from '@mui/material';
import { PdfProductFields } from 'quote-detail/pdf-product-fields';
import { useEffect } from 'react';
import { FormProvider, useFieldArray, useForm } from 'react-hook-form';
import { useQuoteState } from 'shared/contexts';
import { CheckBoxField, FormTextField, PhoneTextField } from 'shared/controls';
import { usePdfExport, useUpdateQuote } from 'shared/mutations';
import { PdfExport, PdfForm, Quote } from 'shared/types';
import { buildExportBrandName, pdfFormToExport, quoteToDefaultPdfForm, updateQuoteFromPdfForm } from 'shared/utility';
import { BrandContactDropdown } from './brand-contact-dropdown';
import { PdfExpenseFields } from './pdf-expense-fields';
import { Brands } from 'shared/types';
import { useParams } from 'react-router-dom';
import { useGetBillToAddress } from 'shared/queries/get-bill-address';
import { useToggle } from 'shared/hooks';
import { PdfCustomProductFields } from './pdf-custom-product-fields';

interface PdfDialogProps {
  quote: Quote;
}
export function PdfDialog({ quote }: PdfDialogProps) {
  const defaultExportQuote = quoteToDefaultPdfForm(quote);
  const methods = useForm<PdfForm>({ defaultValues: defaultExportQuote });
  const pdfMutation = usePdfExport<PdfExport>();
  const { pdfDialogOpen, togglePdfDialog } = useQuoteState();
  const updateMutation = useUpdateQuote();
  const { brand } = useParams<{ brand: Brands }>();
  const isIndelcoBrand = brand === 'indelco';
  const isQualityStainless = brand === 'qualitystainless';
  const [summarizedExpenses, setSummarizedExpenses] = useToggle(true);

  const {
    handleSubmit,
    control,
    watch,
    formState: { errors },
    reset,
    setValue,
    getValues,
  } = methods;

  const name = getValues().customerId;
  const { address } = useGetBillToAddress(name, brand);
  const sameAsShipTo = watch('sameAsShipTo');

  useEffect(() => {
    const formData = quoteToDefaultPdfForm(quote);
    reset(formData);

    if (formData.expenses.length > 0) {
      const expensesTotalValue = formData.expenses
        .map(expense => expense.extendedPrice)
        .reduce((value, current) => (value += current));

      formData.expenses.push({
        notes: '',
        type: 'Expense Summary',
        quantity: 1,
        extendedPrice: expensesTotalValue,
        price: expensesTotalValue,
        itemized: false,
        lineNumber: formData.expenses.length,
      });
    }

    setValue('expenses', formData.expenses);
    setValue('products', formData.products);
  }, [quote, reset, setValue]);

  const { fields, move } = useFieldArray({
    control,
    name: 'products',
  });

  const { fields: customProductFields, move: customProductMove } = useFieldArray({
    control,
    name: 'customProducts',
  });

  const { fields: expenseFields, move: expenseMove } = useFieldArray({
    control,
    name: 'expenses',
  });

  const watchProducts = watch('products');
  const watchCustomProducts = watch('customProducts');
  const watchExpenses = watch('expenses');

  const productControlledFields = fields.map((field, index) => ({
    ...field,
    ...watchProducts[index],
  }));
  const customProductControlledFields = customProductFields.map((field, index) => ({
    ...field,
    ...watchCustomProducts[index],
  }));
  const expenseControlledFields = expenseFields.map((field, index) => ({
    ...field,
    ...watchExpenses[index],
  }));

  const summarizedExpensesField = watchExpenses[expenseControlledFields.length];

  useEffect(() => {
    if (address) {
      setValue('billToAddressLine1', address);
      setValue('billToAddressLine2', '');
    }
  }, [address, setValue]);

  useEffect(() => {
    if (isQualityStainless) {
      setValue('quoteTerms', 'Standard Terms and Conditions Apply - Pricing valid for 24 hours');
    }
  }, [isQualityStainless, setValue]);

  useEffect(() => {
    const form = getValues();

    if (sameAsShipTo) {
      setValue('billToName', form.shipToName);
      setValue('billToAddressLine1', form.shipToAddressLine1);
      setValue('billToAddressLine2', form.shipToAddressLine2);
    } else if (address) {
      setValue('billToAddressLine1', address);
      setValue('billToAddressLine2', '');
    } else {
      setValue('billToName', form.billToName);
      setValue('billToAddressLine1', form.billToAddressLine1);
      setValue('billToAddressLine2', form.billToAddressLine2);
    }
  }, [sameAsShipTo, setValue, getValues, address]);

  const onSubmit = async (data: PdfForm) => {
    let expensesFiltered = [...getValues('expenses').filter(exp => exp.itemized)];
    if (summarizedExpenses) {
      expensesFiltered = [...getValues('expenses').filter(exp => !exp.itemized)];
    }

    if (expensesFiltered.some(e => !e.type)) {
      return;
    } else {
      data.expenses = expensesFiltered;

      const exportData = pdfFormToExport(data);
      exportData.brand = buildExportBrandName(exportData.brand, exportData.name);

      await pdfMutation.mutateAsync({
        exportId: 'quote',
        data: exportData,
        fileName: `${exportData.quoteNumber}-${exportData.customerId}`,
      });
      const newQuote: Quote = updateQuoteFromPdfForm(data, quote);
      updateMutation.mutate(newQuote);

      togglePdfDialog();
    }
  };

  const cancel = () => {
    togglePdfDialog();
  };

  const moveUp = (index: number) => () => {
    move(index, index - 1);
  };
  const moveDown = (index: number) => () => {
    move(index, index + 1);
  };

  const customProductMoveUp = (index: number) => () => {
    customProductMove(index, index - 1);
  };
  const customProductMoveDown = (index: number) => () => {
    customProductMove(index, index + 1);
  };

  const expenseMoveUp = (index: number) => () => {
    expenseMove(index, index - 1);
  };
  const expenseMoveDown = (index: number) => () => {
    expenseMove(index, index + 1);
  };

  const exportButtonText = pdfMutation.isLoading ? 'exporting...' : 'export';
  const disableExport = pdfMutation.isLoading;

  return (
    <Dialog fullWidth maxWidth={false} open={pdfDialogOpen}>
      <DialogTitle>Export Pdf</DialogTitle>
      <DialogContent>
        <FormProvider {...methods}>
          <form id='export-form' onSubmit={handleSubmit(onSubmit)}>
            <Grid container spacing={2}>
              <Grid item xs={12}>
                <fieldset>
                  <legend>Quote Details</legend>
                  <Grid container spacing={1}>
                    <Grid item xs={12} md={3}>
                      <FormTextField label='Modified Quote Number' name='modifiedQuoteNumber' />
                    </Grid>
                    <Grid item xs={12} md={3}>
                      <BrandContactDropdown />
                    </Grid>
                    <Grid item xs={12} md={3}>
                      <FormTextField label='RFQ Number' name='rfqNumber' />
                    </Grid>
                    <Grid item xs={12} md={3}>
                      <FormTextField label='Requested By' name='requestedBy' />
                    </Grid>
                    <Grid item xs={12} md={3}>
                      <PhoneTextField
                        label='Customer Phone Number'
                        name='billToPhone'
                        rules={{
                          pattern: {
                            message: 'Phone Number is incomplete',
                            value: /^\(([0-9]{3})\)[ ]([0-9]{3})[-]([0-9]{4})$/,
                          },
                        }}
                        error={Boolean(errors?.billToPhone)}
                        helperText={errors?.billToPhone?.message}
                      />
                    </Grid>
                    <Grid item xs={12} md={3}>
                      <FormTextField label='Ship To Name' name='shipToName' />
                    </Grid>
                    <Grid item xs={12} md={3}>
                      <FormTextField label='Ship To Address Line 1' name='shipToAddressLine1' />
                    </Grid>
                    <Grid item xs={12} md={3}>
                      <FormTextField label='Ship To Address Line 2' name='shipToAddressLine2' />
                    </Grid>
                    <Grid item xs={12} md={3}>
                      <CheckBoxField label='Bill To same as Ship To' name='sameAsShipTo' />
                    </Grid>
                    <Grid item xs={12} md={3}>
                      <FormTextField label='Bill To Name' name='billToName' />
                    </Grid>
                    <Grid item xs={12} md={3}>
                      <FormTextField label='Bill To Address Line 1' name='billToAddressLine1' />
                    </Grid>
                    <Grid item xs={12} md={3}>
                      <FormTextField label='Bill To Address Line 2' name='billToAddressLine2' />
                    </Grid>
                    <Grid item xs={12} md={3}>
                      <FormTextField label='Quote Terms' name='quoteTerms' disabled={isIndelcoBrand} />
                    </Grid>
                    <Grid item xs={12} md={3}>
                      <FormTextField label='Payment Terms' name='paymentTerms' />
                    </Grid>
                    <Grid item xs={12} md={3}>
                      <FormTextField label='Freight Terms' name='freightTerms' />
                    </Grid>
                    <Grid item xs={12} md={3}>
                      <FormTextField label='Delivery' name='delivery' />
                    </Grid>
                    <Grid item xs={12} md={3}>
                      <FormTextField
                        rules={{
                          required: {
                            value: true,
                            message: 'Quoted By is required.',
                          },
                        }}
                        label='Quoted By'
                        name='quotedBy'
                        error={Boolean(errors?.quotedBy)}
                        helperText={errors?.quotedBy?.message}
                      />
                    </Grid>
                    <Grid item xs={12} md={3}>
                      <FormTextField
                        rules={{
                          required: {
                            value: true,
                            message: 'Quoted By Email is required.',
                          },
                        }}
                        label='Quoted By Email'
                        name='quotedByEmail'
                        error={Boolean(errors?.quotedBy)}
                        helperText={errors?.quotedBy?.message}
                      />
                    </Grid>
                    <Grid item xs={12} md={3}>
                      <PhoneTextField label='Quoted By Phone Number' name='quotedByPhoneNumber' />
                    </Grid>
                    <Grid item xs={12} md={3} />
                    <Grid item xs={12} md={3}>
                      <FormTextField label='Sales Rep' name='salesRep' />
                    </Grid>
                    <Grid item xs={12} md={3}>
                      <FormTextField label='Sales Rep Email' name='salesRepEmail' />
                    </Grid>
                    <Grid item xs={12} md={3}>
                      <PhoneTextField label='Sales Rep Phone Number' name='salesRepPhoneNumber' />
                    </Grid>
                    <Grid xs={12} item>
                      <FormTextField label='Quote Notes' name='quoteNotes' multiline maxRows={10} minRows={2} />
                    </Grid>
                  </Grid>
                </fieldset>
              </Grid>
              <Grid item xs={12}>
                <fieldset>
                  <legend>Product Details</legend>
                  <Grid container spacing={1}>
                    <Grid item xs={12} md={3}>
                      <FormTextField label='Product Prefix' name='productPrefix' readOnly={false} />
                    </Grid>
                    {productControlledFields.map((field, index) => (
                      <PdfProductFields
                        key={field.id}
                        product={field}
                        index={index}
                        disableMoveUp={index === 0}
                        disableMoveDown={index === productControlledFields.length - 1}
                        moveDown={moveDown(index)}
                        moveUp={moveUp(index)}
                      />
                    ))}
                  </Grid>
                </fieldset>
              </Grid>

              {customProductControlledFields.length > 0 && (
                <Grid item xs={12}>
                  <fieldset>
                    <legend>Custom Product Details</legend>
                    <Grid container spacing={1}>
                      {customProductControlledFields.map((field, index) => (
                        <PdfCustomProductFields
                          key={field.id}
                          customProduct={field}
                          index={index}
                          disableMoveUp={index === 0}
                          disableMoveDown={index === customProductControlledFields.length - 1}
                          moveDown={customProductMoveDown(index)}
                          moveUp={customProductMoveUp(index)}
                        />
                      ))}
                    </Grid>
                  </fieldset>
                </Grid>
              )}

              {expenseControlledFields.length > 0 ? (
                <Grid item xs={12}>
                  <fieldset>
                    <legend>Expense Summary</legend>
                    <FormControlLabel
                      label='Summarized expenses'
                      control={<Switch checked={summarizedExpenses} onChange={setSummarizedExpenses} />}
                    />
                    <Grid container spacing={1}>
                      {summarizedExpenses ? (
                        <PdfExpenseFields
                          key={'expensesSummary'}
                          expense={summarizedExpensesField}
                          index={expenseControlledFields.length}
                          disableMoveUp={true}
                          disableMoveDown={true}
                          moveDown={expenseMoveDown(0)}
                          moveUp={expenseMoveUp(0)}
                          isSummaryExpense={true}
                        />
                      ) : (
                        expenseControlledFields.map((field, index) => (
                          <PdfExpenseFields
                            key={field.id}
                            expense={field}
                            index={index}
                            disableMoveUp={index === 0}
                            disableMoveDown={index === expenseControlledFields.length - 1}
                            moveDown={expenseMoveDown(index)}
                            moveUp={expenseMoveUp(index)}
                          />
                        ))
                      )}
                    </Grid>
                  </fieldset>
                </Grid>
              ) : (
                <></>
              )}
            </Grid>
          </form>
        </FormProvider>
      </DialogContent>
      <DialogActions>
        <Button variant='contained' onClick={cancel}>
          cancel
        </Button>
        <Button variant='contained' type='submit' form='export-form' disabled={disableExport}>
          {exportButtonText}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
